/**
 * shared.tsx — small pieces every callbacks component leans on (v0.6.5).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the callbacks tab is split across a board, a list, a drawer, a
 * timeline and a composer — the display-name composition, the outcome label
 * maps, the preferred-time render rule and the segmented-control style must
 * read identically in all of them, so they live here once.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { CallAttemptOutcome, CallbackActivity, CallbackOutcome, CallbackSubmission } from '../../api/types';

/** Compose a display name from the two contracts name fields (may both be null). */
export function contactName(item: CallbackSubmission): string {
  const parts = [item.firstName, item.lastName].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' ') : 'Not recorded';
}

/** The CLOSE outcomes (how a ticket was resolved — distinct from call outcomes). */
export const OUTCOME_OPTIONS: Array<{ value: CallbackOutcome; label: string }> = [
  { value: 'video_appointment', label: 'Video appointment' },
  { value: 'online_booking', label: 'Online booking' },
  { value: 'not_proceeding', label: 'Not proceeding' },
  { value: 'escalated_to_clinician', label: 'Escalated to clinician' },
  { value: 'other', label: 'Other' },
];

export const OUTCOME_LABEL: Record<CallbackOutcome, string> = OUTCOME_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<CallbackOutcome, string>,
);

/**
 * The call-attempt outcomes the composer offers (contracts order).
 *
 * ⚠️ `call_back_later` WAS MISSING, AND ITS ABSENCE WAS PARKING PATIENTS
 * (2026-09-06). clinic-service has accepted it since 2026-09-05 and treats it
 * as THE exception to the parking counter: a ticket parks after three strikes
 * per owner and a parked deal drops into `unreachable`, where the nurture lane
 * starts emailing the patient to come back and finish a journey they never
 * abandoned. Only genuine NON-CONTACT is supposed to spend a strike, and a
 * patient who picks up and says "call me Tuesday" is the most engaged person in
 * the queue. With no option for it, staff logging that call had to pick
 * "Answered" — which DOES advance parking — so the estate was chasing its most
 * co-operative patients as if they had ignored the phone.
 *
 * Last in the list on purpose: the four above it are what a caller reaches for
 * most, and this one is the deliberate choice a person makes after a
 * conversation, not a default they might land on by accident.
 */
export const CALL_OUTCOME_OPTIONS: Array<{ value: CallAttemptOutcome; label: string }> = [
  { value: 'answered', label: 'Answered' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'busy', label: 'Busy' },
  { value: 'disconnected', label: 'Disconnected' },
  { value: 'call_back_later', label: 'Answered — asked us to call back later' },
];

/**
 * Lower-case outcome text for the timeline chip ("Called — no answer").
 *
 * ⚠️ A `Record<CallAttemptOutcome, …>` ON PURPOSE, NOT A PARTIAL. This map is
 * indexed by an outcome that arrives OFF THE WIRE, and `activityLabel` below
 * interpolates the result straight into a string — so a member missing here
 * does not fall back, it renders the literal text "Called — undefined" on a
 * clinician's timeline. That is exactly what a `call_back_later` row did until
 * today. The exhaustive Record makes the next widening of the union a COMPILE
 * ERROR here rather than a sentence a patient's record tells wrongly.
 */
const CALL_OUTCOME_TEXT: Record<CallAttemptOutcome, string> = {
  answered: 'answered',
  no_answer: 'no answer',
  voicemail: 'voicemail',
  busy: 'busy',
  disconnected: 'disconnected',
  call_back_later: 'answered, call back later',
};

/**
 * The timeline chip text for an entry. NOT `labelFor` — activity kinds are
 * log-entry types, not lifecycle states, and the call chip folds the outcome
 * into one readable phrase.
 */
export function activityLabel(activity: CallbackActivity): string {
  if (activity.kind === 'call_attempt') return `Called — ${CALL_OUTCOME_TEXT[activity.outcome ?? 'no_answer']}`;
  if (activity.kind === 'email') return 'Email logged';
  return 'Note';
}

/**
 * Preferred time renders VERBATIM — the '—' placeholder is ONLY for a
 * null/absent value (older/API-created rows never captured it), and carries a
 * tooltip saying so, so staff don't chase a "missing" answer the patient was
 * never asked for.
 */
export function PreferredTime({ value }: { value?: string | null }) {
  if (value == null) return <span title="Not captured on this request">—</span>;
  return <>{value}</>;
}

/** One segment of a segmented control (view toggle / status filters / composer kinds). */
export function segmentStyle(active: boolean): CSSProperties {
  return {
    minHeight: '44px',
    padding: '8px 16px',
    borderRadius: 'var(--gps-r-pill)',
    border: `1.5px solid ${active ? 'var(--gps-primary)' : 'var(--gps-border-strong)'}`,
    background: active ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
    color: active ? 'var(--gps-green-text)' : 'var(--gps-text)',
    fontWeight: 600,
    fontSize: '13.5px',
    cursor: 'pointer',
  };
}

export const actionLinkStyle: CSSProperties = {
  minHeight: '44px',
  padding: '0 4px',
  background: 'none',
  border: 0,
  color: 'var(--gps-link)',
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
};

/** An inline (non-full-width) action button style on the kit Button. */
export const compactButtonStyle: CSSProperties = { width: 'auto', minHeight: '44px', padding: '10px 18px' };

/** A label/value line in the drawer's contact block. */
export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--gps-s-3)', padding: '5px 0', fontSize: '14px', borderBottom: '1px solid var(--gps-border)' }}>
      <span style={{ color: 'var(--gps-muted)' }}>{label}</span>
      <span style={{ color: 'var(--gps-text)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
